const loginWithPassword = async (config, {
  user,
  password,
  code
}) => {
  const response = await config.api.post("/api/v1/login", {
    user,
    password,
    code
  });
  return response.data;
};

const loginWithOAuthServiceToken = async (config, credentials) => {
  const response = await config.api.post("/api/v1/login", credentials);
  return response.data;
};

const loginWithResumeToken = async (config, credentials) => {
  const response = await config.api.post("/api/v1/login", credentials);
  return response.data;
};

class ApiError extends Error {
  response;
  constructor(response, message, options, ...other) {
    super(message, options, ...other);
    this.response = response;
  }
}
class Api {
  baseUrl;
  constructor(baseUrl) {
    this.baseUrl = baseUrl;
  }
  getFetchConfig = (config) => {
    const headers = {
      "Content-Type": "application/json",
      ...config?.headers || {}
    };
    const requestInit = {
      ...config,
      headers
    };
    return requestInit;
  };
  async request(method = "GET", endpoint, data, config) {
    const url = new URL(endpoint, this.baseUrl).toString();
    const response = await fetch(url, {
      body: data ? JSON.stringify(data) : void 0,
      method,
      headers: {
        "Content-Type": "application/json"
      }
    });
    if (!response.ok) {
      throw new ApiError(response, "Failed Api Request for " + endpoint);
    }
    const jsonData = await response.json();
    return { data: jsonData };
  }
  async post(endpoint, data, config = {}) {
    return this.request("POST", endpoint, data, this.getFetchConfig(config));
  }
  async get(endpoint, config = {}) {
    return this.request("GET", endpoint, null, this.getFetchConfig(config));
  }
  async put(endpoint, data, config = {}) {
    return this.request("PUT", endpoint, data, this.getFetchConfig(config));
  }
  async delete(endpoint, config = {}) {
    return this.request("DELETE", endpoint, null, this.getFetchConfig(config));
  }
}

const ROCKETCHAT_APP_ID = "4c977b2e-eda2-4627-8bfe-2d0358304a79";

const getRCAppBaseURL = (host) => {
  const url = new URL(`api/apps/public/${ROCKETCHAT_APP_ID}`, host);
  return url.toString();
};

const getRCAppInfo = async (host) => {
  const rcAppBaseUrl = getRCAppBaseURL(host);
  const infoUrl = rcAppBaseUrl + "/info";
  const response = await fetch(infoUrl.toString());
  if (!response.ok) {
    return null;
  }
  const info = await response.json();
  return info;
};

const getRCAuthorizeURL = (host, redirectUri, clientId) => {
  const url = new URL(`oauth/authorize`, host);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", clientId);
  url.searchParams.set("redirect_uri", redirectUri);
  url.searchParams.set("state", encodeURIComponent(window.location.origin));
  return url.toString();
};

const loginWithRocketChatOAuth = async (config) => {
  const appInfo = await getRCAppInfo(config.api.baseUrl);
  if (!appInfo) {
    throw new Error("EmbeddedChatApp not found on server");
  }
  const { client_id, serviceName, allowedOrigins, redirect_uri } = appInfo.config;
  if (!client_id) {
    throw new Error(
      "client_id not found. Make sure you have configured the EmbeddedChatApp on Rocket.Chat server"
    );
  }
  if (!serviceName) {
    throw new Error(
      "custom_oauth_name not found. Make sure you have configured the EmbeddedChatApp on Rocket.Chat server"
    );
  }
  if (!redirect_uri) {
    throw new Error(
      "redirect_uri not found. Make sure you have configured the EmbeddedChatApp on Rocket.Chat server"
    );
  }
  if (allowedOrigins.length && allowedOrigins.includes(window.location.origin)) {
    throw new Error(
      "Origin not allowed. Make sure you have configured the EmbeddedChatApp on Rocket.Chat server"
    );
  }
  const authorizeUrl = await getRCAuthorizeURL(
    config.api.baseUrl,
    redirect_uri,
    client_id
  );
  let params = `scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,
width=800,height=600,left=-1000,top=-1000,rel=opener`;
  const popup = window.open(authorizeUrl, "Login", params);
  return new Promise((resolve) => {
    if (popup) {
      const onMessage = async (e) => {
        if (e.data.type === "rc-oauth-callback") {
          const { accessToken, expiresIn, serviceName: serviceName2 } = e.data.credentials;
          const response = await config.api.post("/api/v1/login", {
            accessToken,
            expiresIn,
            serviceName: serviceName2
          });
          popup.close();
          resolve(response.data);
        }
      };
      window.addEventListener("message", onMessage);
      const checkInterval = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkInterval);
          window.removeEventListener("message", onMessage);
        }
      }, 1e3);
    } else {
      throw new Error("Popup blocked");
    }
  });
};

class RocketChatAuth {
  host;
  api;
  currentUser;
  lastFetched;
  authListeners = [];
  deleteToken;
  saveToken;
  getToken;
  constructor({
    host,
    saveToken,
    getToken,
    deleteToken,
    autoLogin = true
  }) {
    this.host = host;
    this.api = new Api(host);
    this.lastFetched = /* @__PURE__ */ new Date(0);
    this.currentUser = null;
    this.getToken = getToken;
    this.saveToken = saveToken;
    this.deleteToken = deleteToken;
    if (autoLogin) {
      this.load();
    }
  }
  /**
   * Add a callback that will be called when user login status changes
   * @param callback
   */
  async onAuthChange(callback) {
    this.authListeners.push(callback);
    const user = await this.getCurrentUser();
    callback(user);
  }
  async removeAuthListener(callback) {
    this.authListeners = this.authListeners.filter((cb) => cb !== callback);
  }
  notifyAuthListeners() {
    this.authListeners.forEach((cb) => cb(this.currentUser));
  }
  /**
   * Login with username and password
   * @param credentials
   * @returns
   */
  async loginWithPassword({
    user,
    password,
    code
  }) {
    const response = await loginWithPassword(
      {
        api: this.api
      },
      {
        user,
        password,
        code
      }
    );
    this.setUser(response.data);
    return this.currentUser;
  }
  /**
   * Login with OAuthService's accessToken. The service must be configured in RocketChat.
   * @param credentials
   * @returns
   */
  async loginWithOAuthServiceToken(credentials) {
    const response = await loginWithOAuthServiceToken(
      {
        api: this.api
      },
      credentials
    );
    this.setUser(response.data);
    return this.currentUser;
  }
  /**
   * Login with RocketChat OAuth. The EmbeddedChatApp must be installed and configured in RocketChat.
   * @returns
   */
  async loginWithRocketChatOAuth() {
    if (typeof window === "undefined") {
      throw new Error("loginWithRocketChatOAuth can only be called in browser");
    }
    const response = await loginWithRocketChatOAuth({
      api: this.api
    });
    this.setUser(response.data);
    return this.currentUser;
  }
  /**
   * Login with resume token
   * @param resume Previous issued authToken
   * @returns
   */
  async loginWithResumeToken(resume) {
    const response = await loginWithResumeToken(
      {
        api: this.api
      },
      {
        resume
      }
    );
    this.setUser(response.data);
    return this.currentUser;
  }
  /**
   * Get current user.
   * @param refresh
   * @returns
   */
  async getCurrentUser(refresh = false) {
    if (this.currentUser && this.currentUser.authToken) {
      if (refresh || /* @__PURE__ */ new Date() >= new Date(this.lastFetched.getTime() + 354e4)) {
        try {
          await this.loginWithResumeToken(this.currentUser.authToken);
        } catch (e) {
          if (e instanceof ApiError && e.response?.status === 401) {
            await this.logout();
          }
        }
      }
    }
    return this.currentUser;
  }
  /**
   * Set current user
   * @param user
   */
  async setUser(user) {
    this.lastFetched = /* @__PURE__ */ new Date();
    this.currentUser = user;
    await this.save();
  }
  async save() {
    await this.saveToken(this.currentUser.authToken);
    this.notifyAuthListeners();
  }
  /**
   * Load current user from localStorage
   */
  async load() {
    try {
      const token = await this.getToken();
      if (token) {
        const user = await this.loginWithResumeToken(token);
        if (user) {
          this.lastFetched = /* @__PURE__ */ new Date();
          await this.getCurrentUser();
        }
      }
    } catch (e) {
      console.log("Failed to login user on initial load. Sign in.");
      this.notifyAuthListeners();
    }
  }
  /**
   * Logout current user
   */
  async logout() {
    try {
      await this.api.post(`/api/v1/logout`, void 0, {
        headers: {
          "X-Auth-Token": this.currentUser.authToken,
          "X-User-Id": this.currentUser.userId
        }
      });
    } catch (err) {
      console.error(err);
    } finally {
      await this.deleteToken();
    }
    this.lastFetched = /* @__PURE__ */ new Date(0);
    this.currentUser = null;
    this.notifyAuthListeners();
  }
}

const rocketChatAuth = ({
  host,
  saveToken,
  getToken,
  deleteToken,
  autoLogin
}) => {
  return new RocketChatAuth({
    host,
    saveToken,
    getToken,
    deleteToken,
    autoLogin
  });
};

export { RocketChatAuth, rocketChatAuth };
//# sourceMappingURL=index.mjs.map
